import { memo } from "react";
import type { Watchlist, WatchlistItem } from "@/schemas";
import { WatchlistService } from "../services/watchlist-service";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MoreVertical,
  Bookmark,
  Trash2,
  Pencil,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { WatchlistAssetItem } from "./WatchlistAssetItem";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

interface WatchlistCardProps {
  watchlist: Watchlist;
  items: WatchlistItem[];
  allWatchlists: Watchlist[];
  resolveCurrency: (id?: string) => string;
  onRename: (watchlist: Watchlist) => void;
  onDelete: (watchlist: Watchlist) => void;
  onAssetClick: (item: WatchlistItem) => void;
  onItemsChange: () => void;
  onEditItem: (watchlistId: string, item: WatchlistItem) => void;
  isDragOver?: boolean;
}

export const WatchlistCard = memo(function WatchlistCard({
  watchlist,
  items,
  allWatchlists,
  resolveCurrency,
  onRename,
  onDelete,
  onAssetClick,
  onItemsChange,
  onEditItem,
  isDragOver,
}: WatchlistCardProps) {
  const isEmpty = items.length === 0;

  const { setNodeRef } = useDroppable({
    id: `watchlist-${watchlist.watchlist_id}`,
    data: { type: "watchlist", watchlistId: watchlist.watchlist_id },
  });

  const itemIds = items.map((item) => item.item_id);

  const handleDeleteItem = async (itemId: string) => {
    try {
      await WatchlistService.removeItem(watchlist.watchlist_id, itemId);
      onItemsChange();
      toast.success("Asset removed");
    } catch (error) {
      console.error(error);
      toast.error("Failed to remove asset");
    }
  };

  const handleCopyItem = async (
    item: WatchlistItem,
    targetWatchlistId: string
  ) => {
    try {
      const payload: {
        asset_id: string;
        note?: string;
        target_price?: number | null;
      } = {
        asset_id: item.asset_id,
      };
      if (item.note) payload.note = item.note;
      if (item.target_price !== undefined && item.target_price !== null) {
        payload.target_price = item.target_price;
      }

      await WatchlistService.addItem(targetWatchlistId, payload);

      const targetWatchlist = allWatchlists.find(
        (wl) => wl.watchlist_id === targetWatchlistId
      );
      onItemsChange();
      toast.success(`Copied to "${targetWatchlist?.name}"`);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "Failed to copy");
    }
  };

  const handleMoveItem = async (
    item: WatchlistItem,
    targetWatchlistId: string
  ) => {
    try {
      await WatchlistService.moveItem(
        watchlist.watchlist_id,
        item.item_id,
        targetWatchlistId
      );

      const targetWatchlist = allWatchlists.find(
        (wl) => wl.watchlist_id === targetWatchlistId
      );
      onItemsChange();
      toast.success(`Moved to "${targetWatchlist?.name}"`);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "Failed to move");
    }
  };

  return (
    <Card
      className={cn(
        "flex flex-col",
        isEmpty && "opacity-70 hover:opacity-100",
        isDragOver && "ring-1 ring-primary/40"
      )}
    >
      {/* Header */}
      <CardHeader className="py-3 px-4 flex flex-row items-center justify-between border-b shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Bookmark
            className={cn(
              "h-4 w-4 shrink-0",
              isEmpty ? "text-muted-foreground" : "text-primary"
            )}
          />
          <h3 className="font-semibold text-sm truncate">{watchlist.name}</h3>
          <span className="text-xs text-muted-foreground">
            ({items.length})
          </span>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onRename(watchlist)}>
              <Pencil className="h-3 w-3 mr-2" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => onDelete(watchlist)}
            >
              <Trash2 className="h-3 w-3 mr-2" />
              Delete Watchlist
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>

      <CardContent ref={setNodeRef} className="flex-1 p-0 flex flex-col min-h-0">
        <SortableContext
          items={itemIds}
          strategy={verticalListSortingStrategy}
        >
          {isEmpty ? (
            <div
              className={cn(
                "flex-1 flex flex-col items-center justify-center p-4 text-center text-muted-foreground min-h-[80px] rounded-b-lg",
                isDragOver && "border-2 border-dashed border-primary/30 bg-primary/5"
              )}
            >
              {isDragOver ? (
                <>
                  <Bookmark className="h-6 w-6 mb-2 text-primary/50" />
                  <p className="text-xs text-primary/70 font-medium">Drop here</p>
                </>
              ) : (
                <>
                  <Bookmark className="h-6 w-6 mb-2 opacity-20" />
                  <p className="text-xs">Empty watchlist</p>
                  <p className="text-[10px] mt-0.5">
                    Use the Add Asset button to add assets
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="p-2 space-y-1.5">
              {items.map((item) => (
                <WatchlistAssetItem
                  key={item.item_id}
                  item={item}
                  watchlists={allWatchlists}
                  currentWatchlistId={watchlist.watchlist_id}
                  resolveCurrency={resolveCurrency}
                  onClick={() => onAssetClick(item)}
                  onEdit={() => onEditItem(watchlist.watchlist_id, item)}
                  onDelete={() => handleDeleteItem(item.item_id)}
                  onCopyTo={(targetId) => handleCopyItem(item, targetId)}
                  onMoveTo={(targetId) => handleMoveItem(item, targetId)}
                />
              ))}
              {isDragOver && (
                <div className="flex items-center justify-center rounded-lg border border-dashed border-primary/30 py-2 text-xs font-medium text-primary/70">
                  Drop here
                </div>
              )}
            </div>
          )}
        </SortableContext>
      </CardContent>
    </Card>
  );
});
